import { Box } from '@mui/material'
import * as React from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { A11y, Navigation, Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Swiper as SwiperType } from 'swiper/types'

import 'swiper/css'
import 'swiper/css/navigation'

import { debounce } from '@/shared/lib'
import { Arrow } from '@/shared/ui/Arrow'
import { VodArrowLeft, VodArrowRight } from '@/shared/ui/Icons'
import {
    vodSliderButtonHeight,
    vodSliderButtonWidth,
} from '@/shared/constants/vodConstants'
import { pageType } from '@/shared/store/types'
import VodItemView from '@/features/VOD/components/vodItem/VodItemView'
import { useVodItemsContext } from '@/features/VOD/context/UseGetVodItemContext/useVodItemsContext'
import VodContentSkeleton from '@/features/VOD/components/skeletons/VodContentSkeleton'
import { debounceSpeedMedium } from '@/shared/constants'
import { VodRowViewSkeleton } from '@/features/VOD/components/skeletons/VodRowViewSkeleton'
import { useDetectDevice } from '@/shared/hooks'

interface ISlider {
    buttonHeight?: number

    vodItemHeight: number
    handleGoToCategoryButtonClick: (
        itemId: string,
        title: string,
        type: pageType
    ) => void
    scrollPosition: {
        x: number
        y: number
    }
    hideCaptions?: boolean
    hideProgress?: boolean
    rowHeightPercentage?: number
    cardRadiusDp?: number
    isHomeContentRow?: boolean
}

const VodSlider: React.FC<ISlider> = ({
    buttonHeight,
    vodItemHeight,
    handleGoToCategoryButtonClick,
    scrollPosition,
    hideCaptions,
    hideProgress,
    rowHeightPercentage,
    cardRadiusDp,
    isHomeContentRow,
}) => {
    const swiperRef = useRef<SwiperType | null>(null)
    const [isAtStart, setIsAtStart] = useState(true)
    const [isAtEnd, setIsAtEnd] = useState(false)
    const [isLoadingMore, setIsLoadingMore] = useState(false)
    const currentIndexRef = useRef(0)
    const previousItemsLengthRef = useRef(0)
    const { rowItems, getItems } = useVodItemsContext()
    const { userUseDesktopBrowser } = useDetectDevice()

    const debouncedGetItems = useMemo(
        () =>
            debounce(async () => {
                if (isLoadingMore) return

                setIsLoadingMore(true)
                const currentIndex = swiperRef.current?.activeIndex || 0
                currentIndexRef.current = currentIndex
                previousItemsLengthRef.current = rowItems.length

                try {
                    await getItems()
                } finally {
                    setIsLoadingMore(false)

                    // Only restore position if we actually got new items
                    requestAnimationFrame(() => {
                        if (!swiperRef.current) return

                        const gotNewItems =
                            rowItems.length > previousItemsLengthRef.current

                        if (gotNewItems) {
                            swiperRef.current.slideTo(
                                previousItemsLengthRef.current,
                                0
                            )
                        }
                    })
                }
            }, debounceSpeedMedium),
        [isLoadingMore, getItems, rowItems.length]
    )

    const handleSlideChange = useCallback(() => {
        if (!swiperRef.current) return

        setIsAtStart(swiperRef.current.isBeginning)
        setIsAtEnd(swiperRef.current.isEnd)
        currentIndexRef.current = swiperRef.current.activeIndex
    }, [])

    const handlePrevSlide = useCallback(() => {
        swiperRef.current?.slidePrev()
        handleSlideChange()
    }, [handleSlideChange])

    const handleNextSlide = useCallback(() => {
        if (!swiperRef.current) return
        const swiper = swiperRef.current
        const totalSlides = swiper.slides.length
        const currentIndex = swiper.activeIndex
        const slidesPerView =
            swiper.params.slidesPerView === 'auto'
                ? swiper.slidesPerViewDynamic()
                : Number(swiper.params.slidesPerView)

        // Calculate if we're near the end (3 slides before the end)
        const isNearEnd = totalSlides - (currentIndex + slidesPerView) <= 3

        swiper.slideNext()
        handleSlideChange()

        if (isNearEnd && !isLoadingMore) {
            debouncedGetItems()
        }
    }, [handleSlideChange, debouncedGetItems, isLoadingMore])

    const itemHeight = rowHeightPercentage
        ? vodItemHeight * (rowHeightPercentage / 100)
        : vodItemHeight

    const navigationStyles = {
        arrowBase: {
            minWidth: '48px',
            minHeight: '48px',
            position: 'absolute',
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 9,
            width: buttonHeight || vodSliderButtonHeight,
            aspectRatio: `${buttonHeight || vodSliderButtonWidth} / ${
                buttonHeight || vodSliderButtonWidth
            }`,
            height: buttonHeight || vodSliderButtonHeight,
            maxHeight: itemHeight / 2.5,
            maxWidth: itemHeight / 2.5,
            pointerEvents: 'all' as const,
            cursor: 'pointer',
        },
        leftArrow: {
            left: 0,
        },
        rightArrow: {
            right: 0,
        },
        shadowBox: {
            position: 'absolute',
            width: '100%',
            height: '100%',
            borderRadius: '100%',
            background: 'black',
            opacity: 0.3,
            top: 0,
            left: 0,
            zIndex: 8,
            pointerEvents: 'all' as const,
            cursor: 'pointer',
        },
    }

    useEffect(() => {
        if (!swiperRef.current) return

        const swiper = swiperRef.current
        swiper.on('slideChange', handleSlideChange)
        setIsAtStart(swiper.isBeginning)
        setIsAtEnd(swiper.isEnd)

        return () => {
            swiper.off('slideChange', handleSlideChange)
        }
    }, [handleSlideChange, rowItems])

    if (!rowItems.length) {
        return <VodContentSkeleton rows={1} items={6} />
    }

    return (
        <Box position="relative">
            <Swiper
                onBeforeInit={(swiper: SwiperType) => {
                    swiperRef.current = swiper
                }}
                modules={[Navigation, Pagination, A11y]}
                spaceBetween={16}
                slidesPerGroup={userUseDesktopBrowser ? 2 : 1}
                slidesPerView="auto"
                observer={true}
                observeParents={true}
                onSlideChange={handleSlideChange}>
                {rowItems.length ? (
                    rowItems.map((item, index) => (
                        <SwiperSlide
                            key={`${item.id}-${index}`}
                            style={{ width: 'min-content' }}>
                            <VodItemView
                                key={`${item.id}-${index}`}
                                hideCaptions={hideCaptions}
                                hideProgress={hideProgress}
                                vodItemHeight={vodItemHeight}
                                cardRadiusDp={cardRadiusDp}
                                rowHeightPercentage={rowHeightPercentage}
                                item={item}
                                handleGoToCategoryButtonClick={
                                    handleGoToCategoryButtonClick
                                }
                                scrollPosition={scrollPosition}
                                isHomeContentItem={isHomeContentRow}
                            />
                        </SwiperSlide>
                    ))
                ) : (
                    <VodRowViewSkeleton maxHeight={258} rows={1} items={7} />
                )}
            </Swiper>

            <Box
                position="absolute"
                top="0"
                left="0"
                height={itemHeight}
                width="100%"
                sx={{
                    display: {
                        xs: 'none', // ukryj na ekranach mobilnych
                        sm: 'block', // pokaż od breakpointu "sm" wzwyż
                    },
                }}>
                <Box
                    sx={{
                        ...navigationStyles.arrowBase,
                        ...navigationStyles.leftArrow,
                        opacity: isAtStart ? 0 : 1,
                        visibility: isAtStart ? 'hidden' : 'visible',
                    }}
                    onClick={handlePrevSlide}>
                    <Arrow icon={<VodArrowLeft />} />
                    <Box sx={navigationStyles.shadowBox} />
                </Box>

                <Box
                    sx={{
                        ...navigationStyles.arrowBase,
                        ...navigationStyles.rightArrow,
                        opacity: isAtEnd ? 0 : 1,
                        visibility: isAtEnd ? 'hidden' : 'visible',
                    }}
                    onClick={handleNextSlide}>
                    <Arrow icon={<VodArrowRight />} />
                    <Box sx={navigationStyles.shadowBox} />
                </Box>
            </Box>
        </Box>
    )
}

export default VodSlider
